import React from 'react';

import { Box, Badge as UCLBadge } from '@rbilabs/universal-components';
import { Tabs } from 'expo-router';
import { useIntl } from 'react-intl';
import { Image, StyleSheet } from 'react-native';
import { TouchableOpacity } from 'react-native';

import { StandardMobileHeader } from 'components/app-header/mobile-header';
import { useIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import {
  ClickEventComponentNames,
  CustomEventNames,
  EventTypes,
  logRBIEvent,
} from 'state/amplitude';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { primitive } from 'styles/constants/primitives';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { isIos } from 'utils/environment';

export const unstable_settings = {
  initialRouteName: 'index',
};

export default function TabsLayout() {
  const { formatMessage } = useIntl();
  const isDesktopWeb = useIsDesktopWebBreakpoint();

  return (
    <Tabs
      sceneContainerStyle={{ backgroundColor: primitive.bk.$mayoEggWhite }}
      screenOptions={{
        header: WrappedStandardMobileHeader,
        tabBarButton: TouchFeedbackTabBarIcon,
        tabBarStyle: tabBarStyles.tabBarStyle,
        tabBarLabelStyle: tabBarStyles.tabBarLabelStyle,
      }}
    >
      <Tabs.Screen
        name="index"
        {...getAnalyticsEvent('Home', '/')}
        options={{
          header: () => <StandardMobileHeader hideTsm={!isDesktopWeb} />,
          title: formatMessage({ id: 'navHome' }),
          tabBarTestID: getTestId('Home'),
          tabBarIcon: ({ focused }) => <TabBarIcon name="home" focused={focused} />,
        }}
      />
      <Tabs.Screen
        name="menu"
        {...getAnalyticsEvent('Menu', '/menu')}
        options={{
          title: formatMessage({ id: 'navMenu' }),
          tabBarTestID: getTestId('Menu'),
          headerShown: false,
          tabBarIcon: ({ focused }) => <TabBarIcon name="menu" focused={focused} />,
        }}
      />
      <Tabs.Screen
        name="my-code"
        {...getAnalyticsEvent('My Code', '/my-code')}
        options={{
          title: formatMessage({ id: 'navMyCode' }),
          tabBarTestID: getTestId('My-Code'),
          tabBarButton: isDesktopWeb ? () => null : undefined, // hide for desktop
          tabBarIcon: ({ focused }) => (
            <TabBarIcon name="myCode" focused={focused} children={<MyCodeBadge />} />
          ),
        }}
      />
      <Tabs.Screen
        name="offers"
        {...getAnalyticsEvent('Offers', '/offers')}
        options={{
          title: formatMessage({ id: 'navOffers' }),
          tabBarTestID: getTestId('Offers'),
          tabBarIcon: ({ focused }) => <TabBarIcon name="offers" focused={focused} />,
        }}
      />
      <Tabs.Screen
        name="rewards"
        {...getAnalyticsEvent('Rewards', '/rewards')}
        options={{
          title: formatMessage({ id: 'navRewards' }),
          tabBarTestID: getTestId('Rewards'),
          tabBarIcon: ({ focused }) => <TabBarIcon name="rewards" focused={focused} />,
        }}
      />
    </Tabs>
  );
}

const getTestId = (name: string) => `nav-with-picture-icon-${name}`;

const getAnalyticsEvent = (name: string, route: string) => ({
  listeners: {
    tabPress: () => {
      //this used to use logButtonClick with logDuplicateClickEvent but dupes seem to be no longer necessary. add back if complaints
      logRBIEvent({
        name: CustomEventNames.CLICK_EVENT,
        type: EventTypes.Other,
        attributes: {
          text: name,
          component: ClickEventComponentNames.NAV_BAR,
          componentId: route,
        },
      });
    },
  },
});

const TouchFeedbackTabBarIcon = (props: any) => <TouchableOpacity activeOpacity={0.3} {...props} />;

// for some reason this needs to be wrapped this way. don't change it.
const WrappedStandardMobileHeader = () => <StandardMobileHeader />;

const TAB_IMAGES = {
  home: {
    base: require('src/assets/images/nav/home.png'),
    active: require('src/assets/images/nav/home-active.png'),
  },
  menu: {
    base: require('src/assets/images/nav/menu.png'),
    active: require('src/assets/images/nav/menu-active.png'),
  },
  myCode: {
    base: require('src/assets/images/nav/my-code.png'),
    active: require('src/assets/images/nav/my-code-active.png'),
  },
  offers: {
    base: require('src/assets/images/nav/offers.png'),
    active: require('src/assets/images/nav/offers-active.png'),
  },
  rewards: {
    base: require('src/assets/images/nav/rewards.png'),
    active: require('src/assets/images/nav/rewards-active.png'),
  },
};

const TabBarIcon: React.FC<React.PropsWithChildren<{ name: string; focused: boolean }>> = ({
  name,
  focused,
  children,
}) => {
  return (
    <>
      <Image
        source={focused ? TAB_IMAGES[name].active : TAB_IMAGES[name].base}
        resizeMode="contain"
        style={{
          height: 20,
          width: 20,
        }}
        {...hiddenAccessibilityPlatformProps}
      />
      {children}
    </>
  );
};

const MyCodeBadge = () => {
  const { inRestaurantRedemptionCart } = useInRestaurantRedemptionContext();
  const count = inRestaurantRedemptionCart.reduce(
    (acc, currentValue) => acc + currentValue.quantity,
    0
  );

  if (!count) {
    return null;
  }

  return (
    <BadgeOuter>
      <BadgeInner variant="number-solid">{count.toString()}</BadgeInner>
    </BadgeOuter>
  );
};

const BadgeOuter = Box.withConfig({
  position: 'absolute',
  zIndex: 1,
  top: -5,
  right: 5,
});

const BadgeInner = UCLBadge.withConfig({
  bgColor: primitive.bk.$meltyYellow,
  _text: {
    fontFamily: 'heading',
    fontSize: 'xs',
    color: primitive.bk.$bbqBrown,
  },
  width: '$5',
  height: '$5',
  style: {
    minWidth: 'auto',
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 0,
  },
});

export const tabBarStyles = StyleSheet.create({
  tabBarStyle: {
    backgroundColor: primitive.bk.$mayoEggWhite,
    // web/android tab bar needs a little more space
    ...(!isIos
      ? {
          height: 55,
          paddingBottom: 6,
          paddingTop: 6,
        }
      : null),
  },
  tabBarLabelStyle: {
    fontFamily: 'Flame-Regular',
    fontSize: 12,
    color: primitive.bk.$bbqBrown,
  },
});
